/**
 * Card grid for one band of the site dashboard.
 *
 * Establishes the container every card inside it measures itself against, and
 * lays those cards out on the shared unit grid. Sections size to the stage they
 * are given rather than to the viewport, so a collapsed context rail widens the
 * cards the moment it closes. Cards in a row share a height, so a band reads as
 * one row rather than a ragged edge.
 *
 * Contains:
 * - DashboardSection — the grid section component (default export)
 */
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import React from 'react';

import { UNIT_CONTAINER_NAME, unitLadderSx } from '@sophocles/views/sites/components/SiteDashboard/dashboardUnits';

/**
 * @param {object}     props
 * @param {string}     [props.title]  - section heading
 * @param {React.node} [props.action] - optional right-aligned action node (e.g. a Button or Link)
 * @param {object}     [props.sx]     - optional MUI sx overrides for the section wrapper
 * @param {React.node} props.children - DashboardCard instances
 */
export default function DashboardSection({ title, action, sx, children }) {
  const sxList = Array.isArray(sx) ? sx : [sx].filter(Boolean);

  return (
    <Box
      component="section"
      sx={[{ mb: 2, containerType: 'inline-size', containerName: UNIT_CONTAINER_NAME }, ...sxList]}
    >
      {(title || action) && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          {title && (
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
          )}
          {action && <Box sx={{ ml: 'auto' }}>{action}</Box>}
        </Box>
      )}
      <Box sx={{ display: 'grid', gap: 2, alignItems: 'stretch', ...unitLadderSx() }}>{children}</Box>
    </Box>
  );
}

DashboardSection.propTypes = {
  title: PropTypes.string,
  action: PropTypes.node,
  sx: PropTypes.oneOfType([PropTypes.array, PropTypes.func, PropTypes.object]),
  children: PropTypes.node,
};
